import { useMemo, useState } from 'react'
import { useData } from '../context/DataContext'
import { PageLoading } from '../components/common/LoadingSpinner'
import ChartCard from '../components/common/ChartCard'
import DataTable from '../components/common/DataTable'
import FilterDropdown from '../components/common/FilterDropdown'
import DateRangePicker from '../components/common/DateRangePicker'
import BarChart from '../components/charts/BarChart'
import LineChart from '../components/charts/LineChart'
import {
  formatCurrency,
  formatPercent,
  formatMonth,
  getCountryName
} from '../utils/formatters'

export default function FinancialTargetsMoM() {
  const { data, computed, loading, error } = useData()
  const [selectedCountry, setSelectedCountry] = useState(null)
  const [startMonth, setStartMonth] = useState(null)
  const [endMonth, setEndMonth] = useState(null)

  const filteredData = useMemo(() => {
    if (!data || !computed) return null

    const { months } = computed
    const start = startMonth || months[Math.max(0, months.length - 6)]
    const end = endMonth || months[months.length - 1]
    const filteredMonths = months.filter(m => m >= start && m <= end)

    // Filter and aggregate targets by month
    let targets = data.financialTargets.filter(t =>
      filteredMonths.includes(t.month)
    )

    if (selectedCountry) {
      targets = targets.filter(t => t.country === selectedCountry)
    }

    // Aggregate by month
    const monthlyData = filteredMonths.map(month => {
      const monthTargets = targets.filter(t => t.month === month)
      return {
        month,
        arrTarget: monthTargets.reduce((sum, t) => sum + (t.arr_target || 0), 0),
        arrActual: monthTargets.reduce((sum, t) => sum + (t.arr_actual || 0), 0),
        netNewTarget: monthTargets.reduce((sum, t) => sum + (t.net_new_arr_target || 0), 0),
        netNewActual: monthTargets.reduce((sum, t) => sum + (t.net_new_arr_actual || 0), 0),
        onboardingTarget: monthTargets.reduce((sum, t) => sum + (t.onboarding_revenue_target || 0), 0),
        onboardingActual: monthTargets.reduce((sum, t) => sum + (t.onboarding_revenue_actual || 0), 0)
      }
    })

    // Calculate variances
    const tableData = monthlyData.map(d => ({
      ...d,
      arrVariance: d.arrActual - d.arrTarget,
      arrVariancePercent: d.arrTarget > 0 ? (d.arrActual - d.arrTarget) / d.arrTarget : 0,
      netNewVariance: d.netNewActual - d.netNewTarget,
      netNewVariancePercent: d.netNewTarget > 0 ? (d.netNewActual - d.netNewTarget) / d.netNewTarget : 0
    }))

    return {
      months: filteredMonths,
      arrTargets: monthlyData.map(d => d.arrTarget),
      arrActuals: monthlyData.map(d => d.arrActual),
      netNewTargets: monthlyData.map(d => d.netNewTarget),
      netNewActuals: monthlyData.map(d => d.netNewActual),
      onboardingActuals: monthlyData.map(d => d.onboardingActual),
      tableData
    }
  }, [data, computed, selectedCountry, startMonth, endMonth])

  if (loading) return <PageLoading />
  if (error) return <div className="text-red-500">Error: {error}</div>
  if (!filteredData) return null

  const {
    months,
    arrTargets,
    arrActuals,
    netNewTargets,
    netNewActuals,
    onboardingActuals,
    tableData
  } = filteredData

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <FilterDropdown
          label="Country"
          value={selectedCountry}
          options={computed.countries.map(c => ({ value: c, label: getCountryName(c) }))}
          onChange={setSelectedCountry}
          placeholder="All Countries"
        />
        <DateRangePicker
          startMonth={startMonth}
          endMonth={endMonth}
          months={computed.months}
          onStartChange={setStartMonth}
          onEndChange={setEndMonth}
        />
      </div>

      {/* Variance Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {tableData.map(d => (
          <div key={d.month} className="card p-4 text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
              {formatMonth(d.month)}
            </p>
            <p className={`text-lg font-bold ${
              d.arrVariance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
            }`}>
              {d.arrVariance >= 0 ? '+' : ''}{formatCurrency(d.arrVariance, { compact: true })}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {formatPercent(d.arrVariancePercent, { showSign: true })}
            </p>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Annual Recurring Revenue" subtitle="Actual vs Budget" height="h-64">
          <BarChart
            labels={months}
            datasets={[
              { label: 'Actual ARR', data: arrActuals, color: '#3b82f6' },
              { label: 'Budget ARR', data: arrTargets, color: '#94a3b8' }
            ]}
          />
        </ChartCard>

        <ChartCard title="Net New ARR" subtitle="Actual vs Budget" height="h-64">
          <BarChart
            labels={months}
            datasets={[
              { label: 'Actual Net New', data: netNewActuals, color: '#22c55e' },
              { label: 'Budget Net New', data: netNewTargets, color: '#94a3b8' }
            ]}
          />
        </ChartCard>
      </div>

      <ChartCard title="Onboarding Revenue" height="h-64">
        <BarChart
          labels={months}
          datasets={[{
            label: 'Onboarding Revenue',
            data: onboardingActuals,
            color: '#8b5cf6'
          }]}
        />
      </ChartCard>

      {/* Detailed Table */}
      <ChartCard title="Monthly Breakdown" height="h-auto">
        <DataTable
          columns={[
            { key: 'month', header: 'Month', render: v => formatMonth(v) },
            { key: 'arrTarget', header: 'ARR Target', render: v => formatCurrency(v) },
            { key: 'arrActual', header: 'ARR Actual', render: v => formatCurrency(v) },
            { key: 'arrVariance', header: 'ARR Variance', render: v => (
              <span className={v >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                {formatCurrency(v)}
              </span>
            )},
            { key: 'arrVariancePercent', header: 'Variance %', render: v => (
              <span className={v >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                {formatPercent(v, { showSign: true })}
              </span>
            )},
            { key: 'netNewTarget', header: 'Net New Target', render: v => formatCurrency(v) },
            { key: 'netNewActual', header: 'Net New Actual', render: v => formatCurrency(v) }
          ]}
          data={tableData}
          pageSize={12}
        />
      </ChartCard>
    </div>
  )
}
